import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Trophy, X, RotateCcw, Play } from 'lucide-react';

export function CatchGameActivity({ mission, onComplete }) {
  const canvasRef = useRef(null);
  const stateRef = useRef(null);
  const rafRef = useRef(null);
  const [phase, setPhase] = useState('ready');
  const [stats, setStats] = useState({ correct: 0, wrong: 0, missed: 0 });
  const doneRef = useRef(false);

  const items = mission.gameItems || [];
  const totalCorrect = items.filter(i => i.correct).length;

  const startGame = () => {
    doneRef.current = false;
    setStats({ correct: 0, wrong: 0, missed: 0 });
    const canvas = canvasRef.current;
    if (!canvas) return;
    const W = canvas.parentElement.clientWidth;
    const H = 420;
    canvas.width = W;
    canvas.height = H;

    stateRef.current = {
      W, H,
      basket: { x: W / 2, w: 120 },
      falling: [],
      queue: [...items].sort(() => Math.random() - 0.5),
      spawnTimer: 0,
      spawnDelay: 55,
      correctCaught: 0,
      wrongCaught: 0,
      missed: 0,
      particles: [],
    };
    setPhase('playing');
  };

  useEffect(() => {
    if (phase !== 'playing') return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const s = stateRef.current;
    if (!s || !ctx) return;

    ctx.font = '13px sans-serif';

    const onMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      const cx = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
      s.basket.x = Math.max(60, Math.min(s.W - 60, cx));
    };
    canvas.addEventListener('mousemove', onMove);
    canvas.addEventListener('touchmove', onMove, { passive: true });

    const loop = () => {
      s.spawnTimer--;
      if (s.spawnTimer <= 0 && s.queue.length > 0) {
        const item = s.queue.pop();
        const tw = ctx.measureText(item.term).width;
        s.falling.push({ ...item, x: 50 + Math.random() * (s.W - 100), y: -20, speed: 1.1 + Math.random() * 1.3, w: Math.max(80, tw + 32) });
        s.spawnTimer = s.spawnDelay;
      }

      s.falling.forEach(f => { f.y += f.speed; });

      const by = s.H - 50;
      s.falling = s.falling.filter(f => {
        if (f.y >= by - 16 && f.y <= by + 16 && Math.abs(f.x - s.basket.x) < (s.basket.w / 2 + f.w / 2)) {
          if (f.correct) { s.correctCaught++; burst(s, f.x, by, '#22c55e'); }
          else { s.wrongCaught++; burst(s, f.x, by, '#ef4444'); }
          setStats({ correct: s.correctCaught, wrong: s.wrongCaught, missed: s.missed });
          return false;
        }
        if (f.y > s.H + 30) {
          if (f.correct) { s.missed++; setStats({ correct: s.correctCaught, wrong: s.wrongCaught, missed: s.missed }); }
          return false;
        }
        return true;
      });

      s.particles = s.particles.filter(p => { p.x += p.vx; p.y += p.vy; p.vy += 0.15; p.life--; return p.life > 0; });

      if (s.falling.length === 0 && s.queue.length === 0) {
        setPhase('done');
        if (!doneRef.current && s.correctCaught >= totalCorrect * 0.5) { doneRef.current = true; onComplete({ type: 'catch-game', correct: s.correctCaught, wrong: s.wrongCaught, missed: s.missed }); }
        return;
      }

      draw(ctx, s);
      rafRef.current = requestAnimationFrame(loop);
    };

    rafRef.current = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(rafRef.current); canvas.removeEventListener('mousemove', onMove); canvas.removeEventListener('touchmove', onMove); };
  }, [phase, totalCorrect, onComplete, items]);

  const burst = (s, x, y, color) => {
    for (let i = 0; i < 8; i++) s.particles.push({ x, y, vx: (Math.random() - 0.5) * 5, vy: -Math.random() * 4, life: 18 + Math.random() * 12, color });
  };

  const draw = useCallback((ctx, s) => {
    const { W, H } = s;
    ctx.fillStyle = '#080c15';
    ctx.fillRect(0, 0, W, H);

    ctx.strokeStyle = 'rgba(100,150,255,0.03)';
    ctx.lineWidth = 1;
    for (let i = 0; i < W; i += 50) { ctx.beginPath(); ctx.moveTo(i, 0); ctx.lineTo(i, H); ctx.stroke(); }
    for (let i = 0; i < H; i += 50) { ctx.beginPath(); ctx.moveTo(0, i); ctx.lineTo(W, i); ctx.stroke(); }

    ctx.font = '13px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    s.falling.forEach(f => {
      ctx.beginPath();
      ctx.roundRect(f.x - f.w / 2, f.y - 15, f.w, 30, 15);
      ctx.fillStyle = 'rgba(80,120,200,0.12)';
      ctx.fill();
      ctx.strokeStyle = 'rgba(100,150,255,0.3)';
      ctx.lineWidth = 1.5;
      ctx.stroke();
      ctx.fillStyle = '#c0d0f0';
      ctx.fillText(f.term, f.x, f.y, f.w - 10);
    });

    const bx = s.basket.x, by = H - 50;
    ctx.beginPath();
    ctx.roundRect(bx - 60, by - 16, 120, 32, 8);
    const g = ctx.createLinearGradient(bx - 60, by - 16, bx + 60, by + 16);
    g.addColorStop(0, 'rgba(59,130,246,0.25)');
    g.addColorStop(1, 'rgba(59,130,246,0.08)');
    ctx.fillStyle = g;
    ctx.fill();
    ctx.strokeStyle = 'rgba(59,130,246,0.6)';
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.fillStyle = '#93c5fd';
    ctx.font = 'bold 12px sans-serif';
    ctx.fillText('CATCH', bx, by);

    s.particles.forEach(p => {
      ctx.globalAlpha = Math.max(0, p.life / 30);
      ctx.beginPath();
      ctx.arc(p.x, p.y, 3, 0, Math.PI * 2);
      ctx.fillStyle = p.color;
      ctx.fill();
    });
    ctx.globalAlpha = 1;
  }, []);

  if (phase === 'done') {
    const passed = stats.correct >= totalCorrect * 0.5;
    return (
      <div className="text-center py-10" data-testid="catch-game-result">
        <div className={`w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center ${passed ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
          {passed ? <Trophy className="w-10 h-10 text-green-400" /> : <X className="w-10 h-10 text-red-400" />}
        </div>
        <h3 className="text-xl font-bold mb-2">{passed ? 'Great Catch!' : 'Keep Trying!'}</h3>
        <div className="flex justify-center gap-6 mb-4 text-sm">
          <div><span className="text-green-400 font-bold">{stats.correct}</span> Correct</div>
          <div><span className="text-red-400 font-bold">{stats.wrong}</span> Wrong</div>
          <div><span className="text-yellow-400 font-bold">{stats.missed}</span> Missed</div>
        </div>
        <Button onClick={startGame} data-testid="catch-restart-btn"><RotateCcw className="w-4 h-4 mr-2" /> Try Again</Button>
      </div>
    );
  }

  return (
    <div data-testid="catch-game-activity">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-muted-foreground">Catch the correct terms! Move the basket with your mouse.</p>
        {phase === 'playing' && (
          <div className="flex gap-3">
            <Badge variant="secondary" className="text-xs text-green-400">{stats.correct} caught</Badge>
            <Badge variant="outline" className="text-xs text-red-400">{stats.wrong} wrong</Badge>
          </div>
        )}
      </div>
      <div className="rounded-xl overflow-hidden border border-border/30 bg-muted dark:bg-[#080c15]">
        <canvas ref={canvasRef} className="w-full block cursor-none" style={{ height: 420 }} />
      </div>
      {phase === 'ready' && (
        <div className="flex justify-center mt-4">
          <Button onClick={startGame} data-testid="catch-start-btn" size="lg"><Play className="w-4 h-4 mr-2" /> Start Game</Button>
        </div>
      )}
    </div>
  );
}
